import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import { 
  getUsers, 
  getFichajes, 
  getSolicitudesAprobadas, 
  BACKEND_URL,
  crearFichajeManual,
  editarFichaje,
  eliminarFichaje,
  getHistorialModificaciones,
  getTodasModificaciones
} from "../../api/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./ControlHorarios.css";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logoFaeca from "../../assets/images/FaecaAGRO360Transparente.png";

const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Formatear fecha a YYYY-MM-DD usando zona horaria LOCAL (no UTC)
// Evita el bug de toISOString() que desplaza la fecha un día en UTC+1/UTC+2
const toLocalDateStr = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Componente de control avanzado de horarios.
 * Permite visualizar, crear, editar y eliminar fichajes, generar reportes en Excel y PDF,
 * ver historial de modificaciones y gestionar fichajes de múltiples empleados.
 * Incluye cálculos de tiempos trabajados, ausencias y generación de informes.
 * 
 * @returns {JSX.Element} Panel completo de control de horarios
 */
export default function ControlHorarios({ fechasIniciales, onFechasConsumidas }) {
  const { token, user } = useContext(AuthContext);
  const [empleados, setEmpleados] = useState([]);
  const [empleadosSeleccionados, setEmpleadosSeleccionados] = useState([]);
  
  // Calcular fechas por defecto: última semana o fechas iniciales externas
  const obtenerFechasSemanaActual = () => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const haceUnaSemana = new Date(hoy);
    haceUnaSemana.setDate(hoy.getDate() - 7);
    return { inicio: haceUnaSemana, fin: hoy };
  };
  
  const fechasDefault = fechasIniciales?.from
    ? { inicio: new Date(fechasIniciales.from + 'T00:00:00'), fin: new Date(fechasIniciales.to + 'T00:00:00') }
    : obtenerFechasSemanaActual();
  const [fechaInicio, setFechaInicio] = useState(fechasDefault.inicio);
  const [fechaFin, setFechaFin] = useState(fechasDefault.fin);
  // Fechas usadas en la última búsqueda (para generar la tabla)
  const [fechaInicioBusqueda, setFechaInicioBusqueda] = useState(fechasDefault.inicio);
  const [fechaFinBusqueda, setFechaFinBusqueda] = useState(fechasDefault.fin);
  const [fichajes, setFichajes] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [showEmpleadosDropdown, setShowEmpleadosDropdown] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [vistaActual, setVistaActual] = useState("calendario"); // "calendario" o "tabla"
  const [paginaActual, setPaginaActual] = useState(1);
  const itemsPorPagina = 30;
  const [modalAcciones, setModalAcciones] = useState(null); // { userId, fecha, registros }
  
  // Estados para gestión de fichajes
  const [modalCrearFichaje, setModalCrearFichaje] = useState(false);
  const [modalEditarFichaje, setModalEditarFichaje] = useState(null);
  const [modalEliminarFichaje, setModalEliminarFichaje] = useState(null);
  const [modalHistorial, setModalHistorial] = useState(null);
  const [historialModificaciones, setHistorialModificaciones] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: "", texto: "" });
  
  // Formulario crear fichaje
  const [formCrear, setFormCrear] = useState({
    empleadoId: "",
    tipo: "entrada",
    fecha: toLocalDateStr(new Date()),
    hora: new Date().toTimeString().split(' ')[0].substring(0, 5),
    motivo: ""
  });
  
  // Formulario editar fichaje
  const [formEditar, setFormEditar] = useState({
    tipo: "",
    fecha: "",
    hora: "",
    motivo: ""
  });
  
  // Formulario eliminar fichaje
  const [motivoEliminar, setMotivoEliminar] = useState("");
  
  // Estados para informe mensual de firma
  const [mesFirma, setMesFirma] = useState(new Date().getMonth() + 1);
  const [anioFirma, setAnioFirma] = useState(new Date().getFullYear());
  const [empleadosFirma, setEmpleadosFirma] = useState([]);
  const [loadingFirma, setLoadingFirma] = useState(false);
  const [mostrarSeccionFirma, setMostrarSeccionFirma] = useState(false);
  const [showEmpleadosFirmaDropdown, setShowEmpleadosFirmaDropdown] = useState(false);
  
  // Ref para evitar re-renders innecesarios del tooltip
  const tooltipTimerRef = useRef(null);
  const lastPositionRef = useRef({ x: 0, y: 0 });
  
  // Handlers de fecha sin delays
  const handleFechaInicioChange = useCallback((fecha) => {
    setFechaInicio(fecha);
  }, []);
  
  const handleFechaFinChange = useCallback((fecha) => {
    setFechaFin(fecha);
  }, []);

  // Cargar empleados y datos iniciales
  useEffect(() => {
    const cargarEmpleados = async () => {
      try {
        const data = await getUsers(token);
        const filtrados = data.filter(u => u.role === "trabajador");
        setEmpleados(filtrados);
        // Seleccionar todos por defecto
        setEmpleadosSeleccionados(filtrados.map(e => e._id));
        
        // Cargar datos de la semana por defecto
        cargarDatos();
      } catch (err) {
      }
    };
    if (token) {
      cargarEmpleados();
    }
  }, [token, user?._id, user?.nombre, user?.primerApellido, user?.segundoApellido]); // Dependencias específicas del usuario

  // Notificar que las fechas externas se consumieron
  useEffect(() => {
    if (fechasIniciales && onFechasConsumidas) onFechasConsumidas();
  }, []);

  // Resetear paginación al cambiar de vista
  useEffect(() => {
    setPaginaActual(1);
  }, [vistaActual]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // Usar requestAnimationFrame para dar prioridad al renderizado del loading
      await new Promise(resolve => requestAnimationFrame(resolve));
      
      // Convertir fechas a formato YYYY-MM-DD
      const fechaInicioStr = toLocalDateStr(fechaInicio);
      const fechaFinStr = toLocalDateStr(fechaFin);
      
      // Guardar las fechas de búsqueda
      setFechaInicioBusqueda(fechaInicio);
      setFechaFinBusqueda(fechaFin);
      
      // Cargar todos los fichajes del rango con una sola petición
      const query = `?from=${fechaInicioStr}&to=${fechaFinStr}`;
      const allFichajes = await getFichajes(token, query);
      
      // Convertir a array si es un objeto pseudo-array (respuesta cacheada viene como objeto)
      let fichajesArray;
      if (Array.isArray(allFichajes)) {
        fichajesArray = allFichajes;
      } else if (allFichajes && typeof allFichajes === 'object') {
        const keys = Object.keys(allFichajes).filter(k => !isNaN(parseInt(k)));
        fichajesArray = keys.map(k => allFichajes[k]);
      } else {
        fichajesArray = [];
      }
      
      // Dar respiro al navegador antes de actualizar el estado
      await new Promise(resolve => requestAnimationFrame(resolve));
      setFichajes(fichajesArray);
      
      // Cargar solicitudes aprobadas
      try {
        const sols = await getSolicitudesAprobadas(token);
        
        await new Promise(resolve => requestAnimationFrame(resolve));
        setSolicitudes(sols);
      } catch (err) {
        setSolicitudes([]);
      }
      
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  // Generar array de fechas del rango
  const generarFechas = () => {
    const fechas = [];
    const inicio = new Date(fechaInicio);
    inicio.setHours(0, 0, 0, 0);
    const fin = new Date(fechaFin);
    fin.setHours(0, 0, 0, 0);
    const current = new Date(inicio);
    
    while (current <= fin) {
      fechas.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return fechas;
  };

  // Formato de día para cabecera: L 15 12
  const formatoDiaCabecera = (fecha) => {
    const dias = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
    const dia = dias[fecha.getDay()];
    const num = fecha.getDate();
    const mes = fecha.getMonth() + 1;
    return `${dia} ${num}/${mes}`;
  };

  // Formatear fecha para tooltip
  const formatearFecha = (fecha) => {
    const d = new Date(fecha);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Formatear hora
  const formatearHora = (fecha) => {
    const d = new Date(fecha);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  // Obtener estado de un empleado en una fecha
  const obtenerEstadoDia = (empleadoId, fecha) => {
    const fechaStr = toLocalDateStr(fecha);
    
    // Verificar fichajes
    const fichajesEmpleado = fichajes.filter(f => {
      const fechaFichaje = toLocalDateStr(f.fecha);
      return String(f.userId?._id || f.userId) === String(empleadoId) && fechaFichaje === fechaStr;
    });
    
    // Verificar si hay solicitud aprobada
    const solicitud = solicitudes.find(s => {
      const usuarioId = String(s.usuario?._id || s.usuario);
      const empleadoIdStr = String(empleadoId);
      if (usuarioId !== empleadoIdStr) return false;
      const desde = toLocalDateStr(s.fechaInicio);
      const hasta = toLocalDateStr(s.fechaFin);
      return fechaStr >= desde && fechaStr <= hasta;
    });
    
    // Si hay fichajes Y solicitud, combinar información
    if (fichajesEmpleado.length > 0 && solicitud) {
      const fichajesEntrada = fichajesEmpleado.filter(f => f.tipo === 'entrada');
      const fichajesSalida = fichajesEmpleado.filter(f => f.tipo === 'salida');
      const entradas = fichajesEntrada.length;
      const salidas = fichajesSalida.length;
      
      let tooltip = '';
      
      // Información de fichajes
      if (fichajesEntrada.length > 0) {
        tooltip += 'Entradas: ' + fichajesEntrada.map(f => formatearHora(f.fecha)).join(', ');
      }
      if (fichajesSalida.length > 0) {
        if (tooltip) tooltip += '\n';
        tooltip += 'Salidas: ' + fichajesSalida.map(f => formatearHora(f.fecha)).join(', ');
      }
      
      // Información de solicitud
      if (tooltip) tooltip += '\n\n';
      if (solicitud.tipo === 'vacaciones') {
        const desde = formatearFecha(solicitud.fechaInicio);
        const hasta = formatearFecha(solicitud.fechaFin);
        tooltip += `Vacaciones: ${desde} - ${hasta}`;
      } else if (solicitud.tipo === 'ausencia') {
        const motivo = solicitud.motivoAusencia || 'No especificado';
        const horaInicio = solicitud.horaInicio || '';
        const horaFin = solicitud.horaFin || '';
        const horario = horaInicio && horaFin ? `${horaInicio} - ${horaFin}` : '';
        tooltip += `Ausencia: ${motivo}`;
        if (horario) tooltip += `\nHorario: ${horario}`;
      }
      
      return {
        tipo: 'mixto',
        color: entradas > salidas ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800',
        texto: `${entradas}/${salidas}`,
        tooltip
      };
    }
    
    // Si solo hay solicitud
    if (solicitud) {
      const desde = formatearFecha(solicitud.fechaInicio);
      const hasta = formatearFecha(solicitud.fechaFin);
      const duracion = `${desde} - ${hasta}`;
      
      let tooltip = '';
      let color = '';
      let texto = '';
      
      if (solicitud.tipo === 'vacaciones') {
        tooltip = `Vacaciones\nDuración: ${duracion}`;
        color = 'bg-blue-100 text-blue-800';
        texto = 'V';
      } else if (solicitud.tipo === 'ausencia') {
        const motivo = solicitud.motivoAusencia || 'No especificado';
        const horaInicio = solicitud.horaInicio || '';
        const horaFin = solicitud.horaFin || '';
        const horario = horaInicio && horaFin ? `${horaInicio} - ${horaFin}` : '';
        tooltip = `Ausencia: ${motivo}`;
        if (horario) tooltip += `\nHorario: ${horario}`;
        tooltip += `\nDuración: ${duracion}`;
        color = 'bg-red-100 text-red-800';
        texto = 'A';
      } else {
        tooltip = `${solicitud.tipo}\nDuración: ${duracion}`;
        color = 'bg-yellow-100 text-yellow-800';
        texto = solicitud.tipo.charAt(0).toUpperCase();
      }
      
      return {
        tipo: solicitud.tipo,
        color,
        texto,
        tooltip
      };
    }
    
    // Si solo hay fichajes
    if (fichajesEmpleado.length > 0) {
      const fichajesEntrada = fichajesEmpleado.filter(f => f.tipo === 'entrada');
      const fichajesSalida = fichajesEmpleado.filter(f => f.tipo === 'salida');
      
      const entradas = fichajesEntrada.length;
      const salidas = fichajesSalida.length;
      
      // Construir tooltip con horas
      let tooltip = '';
      if (fichajesEntrada.length > 0) {
        tooltip += 'Entradas: ' + fichajesEntrada.map(f => formatearHora(f.fecha)).join(', ');
      }
      if (fichajesSalida.length > 0) {
        if (tooltip) tooltip += '\n';
        tooltip += 'Salidas: ' + fichajesSalida.map(f => formatearHora(f.fecha)).join(', ');
      }
      
      return {
        tipo: 'fichado',
        color: entradas > salidas ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800',
        texto: `${entradas}/${salidas}`,
        tooltip
      };
    }
    
    // Sin datos
    return {
      tipo: 'sin-datos',
      color: 'bg-gray-50 text-gray-400',
      texto: '-',
      tooltip: 'Sin registro'
    };
  };

  // Toggle empleado seleccionado
  const toggleEmpleado = (id) => {
    setEmpleadosSeleccionados(prev => 
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  // Toggle empleado para firma
  const toggleEmpleadoFirma = (id) => {
    setEmpleadosFirma(prev =>
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  const toggleTodosFirma = () => {
    if (empleadosFirma.length === empleados.length) {
      setEmpleadosFirma([]);
    } else {
      setEmpleadosFirma(empleados.map(e => e._id));
    }
  };

  // Seleccionar/deseleccionar todos
  const toggleTodos = () => {
    if (empleadosSeleccionados.length === empleados.length) {
      setEmpleadosSeleccionados([]);
    } else {
      setEmpleadosSeleccionados(empleados.map(e => e._id));
    }
  };

  // Memoizar fechas del calendario - usando fechas de búsqueda para evitar recalcular al cambiar inputs
  const fechas = useMemo(() => {
    // Si no hay fechas de búsqueda, retornar array vacío
    if (!fechaInicioBusqueda || !fechaFinBusqueda) return [];
    
    const result = [];
    const inicio = new Date(fechaInicioBusqueda);
    inicio.setHours(0, 0, 0, 0);
    const fin = new Date(fechaFinBusqueda);
    fin.setHours(0, 0, 0, 0);
    const current = new Date(inicio);
    
    while (current <= fin) {
      result.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return result;
  }, [fechaInicioBusqueda, fechaFinBusqueda]);

  // Memorizar empleados filtrados
  const empleadosFiltrados = useMemo(() => {
    return empleados.filter(e => empleadosSeleccionados.includes(e._id));
  }, [empleados, empleadosSeleccionados]);

  // Pre-calcular todos los estados de las celdas (memoizado)
  const estadosCeldas = useMemo(() => {
    const estados = {};
    empleadosFiltrados.forEach(emp => {
      fechas.forEach((fecha, idx) => {
        const key = `${emp._id}-${idx}`;
        estados[key] = obtenerEstadoDia(emp._id, fecha);
      });
    });
    return estados;
  }, [empleadosFiltrados, fechas, fichajes, solicitudes]);

  // Callback optimizado para manejar el hover
  const handleMouseEnter = useCallback((e, cellKey, estado) => {
    // No mostrar tooltip si no hay datos
    if (estado.tipo === 'sin-datos') return;
    
    // Cancelar timer anterior si existe
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    
    // Validar que el elemento aún existe
    if (!e.currentTarget) return;
    
    // Leer del DOM
    const rect = e.currentTarget.getBoundingClientRect();
    const newX = rect.left + rect.width / 2;
    const newY = rect.top - 10;
    
    // Actualizar posición y celda inmediatamente
    lastPositionRef.current = { x: newX, y: newY };
    setTooltipPosition({ x: newX, y: newY });
    setHoveredCell({ key: cellKey, estado });
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Cancelar cualquier tooltip pendiente
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }
    setHoveredCell(null);
  }, []);
  
  // Cleanup del timer al desmontar
  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  // ============ FUNCIONES DE GESTIÓN DE FICHAJES ============
  
  const mostrarMensaje = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje({ tipo: "", texto: "" }), 5000);
  };

  // Crear fichaje manual
  const handleCrearFichaje = async (e) => {
    e.preventDefault();
    
    if (!formCrear.empleadoId || !formCrear.motivo.trim()) {
      mostrarMensaje("error", "Todos los campos son obligatorios");
      return;
    }

    try {
      setLoading(true);
      
      // Combinar fecha y hora
      const fechaHora = new Date(`${formCrear.fecha}T${formCrear.hora}`);
      
      await crearFichajeManual(token, {
        userId: formCrear.empleadoId,
        tipo: formCrear.tipo,
        fecha: fechaHora.toISOString(),
        motivo: formCrear.motivo
      });

      mostrarMensaje("exito", "Fichaje creado correctamente");
      setModalCrearFichaje(false);
      setFormCrear({
        empleadoId: "",
        tipo: "entrada",
        fecha: toLocalDateStr(new Date()),
        hora: new Date().toTimeString().split(' ')[0].substring(0, 5),
        motivo: ""
      });

      // Recargar datos
      await cargarDatos();
    } catch (error) {
      mostrarMensaje("error", error.response?.data?.message || "Error al crear fichaje");
    } finally {
      setLoading(false);
    }
  };

  // Editar fichaje
  const handleEditarFichaje = async (e) => {
    e.preventDefault();
    
    if (!formEditar.motivo.trim()) {
      mostrarMensaje("error", "El motivo es obligatorio");
      return;
    }

    try {
      setLoading(true);
      
      // Combinar fecha y hora si se modificaron
      let fechaHora = null;
      if (formEditar.fecha && formEditar.hora) {
        fechaHora = new Date(`${formEditar.fecha}T${formEditar.hora}`).toISOString();
      }
      
      await editarFichaje(token, modalEditarFichaje._id, {
        tipo: formEditar.tipo || modalEditarFichaje.tipo,
        fecha: fechaHora || modalEditarFichaje.fecha,
        motivo: formEditar.motivo
      });

      mostrarMensaje("exito", "Fichaje actualizado correctamente");
      setModalEditarFichaje(null);
      setFormEditar({ tipo: "", fecha: "", hora: "", motivo: "" });
      
      // Recargar datos
      await cargarDatos();
    } catch (error) {
      mostrarMensaje("error", error.response?.data?.message || "Error al editar fichaje");
    } finally {
      setLoading(false);
    }
  };

  // Eliminar fichaje
  const handleEliminarFichaje = async () => {
    if (!motivoEliminar.trim()) {
      mostrarMensaje("error", "El motivo de eliminación es obligatorio");
      return;
    }

    try {
      setLoading(true);
      
      await eliminarFichaje(token, modalEliminarFichaje._id, motivoEliminar);

      mostrarMensaje("exito", "Fichaje eliminado correctamente");
      setModalEliminarFichaje(null);
      setMotivoEliminar("");
      
      // Recargar datos
      await cargarDatos();
    } catch (error) {
      mostrarMensaje("error", error.response?.data?.message || "Error al eliminar fichaje");
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal de edición
  const abrirModalEditar = (fichaje) => {
    const fechaObj = new Date(fichaje.fecha);
    setFormEditar({
      tipo: fichaje.tipo,
      fecha: toLocalDateStr(fechaObj),
      hora: fechaObj.toTimeString().split(' ')[0].substring(0, 5),
      motivo: ""
    });
    setModalEditarFichaje(fichaje);
  };

  // Ver historial de modificaciones
  const verHistorial = async (empleadoId) => {
    try {
      setLoadingHistorial(true);
      setModalHistorial(empleadoId);
      
      const desde = fechaInicioBusqueda ? toLocalDateStr(fechaInicioBusqueda) : undefined;
      const hasta = fechaFinBusqueda ? toLocalDateStr(fechaFinBusqueda) : undefined;
      
      const historial = await getHistorialModificaciones(token, empleadoId, desde, hasta);
      setHistorialModificaciones(historial);
    } catch (error) {
      mostrarMensaje("error", "Error al cargar historial de modificaciones");
    } finally {
      setLoadingHistorial(false);
    }
  };

  // Función para exportar a Excel
  const exportarExcel = async () => {
    if (fichajes.length === 0 && solicitudes.length === 0) {
      return alert("No hay datos para exportar.");
    }

    setLoadingExport(true);
    
    // Único yield: permitir que el modal se renderice
    await new Promise(resolve => setTimeout(resolve, 50));
    
    try {
      const filas = [];
      
      // Procesamiento síncrono - SIN yields intermedios
      for (const emp of empleadosFiltrados) {
        for (const fecha of fechas) {
          const fechaStr = toLocalDateStr(fecha);
          const fechaDisplay = formatearFecha(fecha);
          
          // Buscar fichajes del empleado en esa fecha
          const fichajesDelDia = fichajes.filter(f => {
            const fFecha = toLocalDateStr(f.fecha);
            const userId = f.userId?._id || f.userId;
            return String(userId) === String(emp._id) && fFecha === fechaStr;
          });
          
          // Buscar solicitud de ese día
          const solicitudDelDia = solicitudes.find(s => {
            const inicio = toLocalDateStr(s.fechaInicio);
            const fin = toLocalDateStr(s.fechaFin);
            const usuarioId = s.usuario?._id || s.usuario;
            return String(usuarioId) === String(emp._id) && fechaStr >= inicio && fechaStr <= fin;
          });

          // Solo añadir fila si hay datos (fichajes o solicitud)
          if (fichajesDelDia.length > 0 || solicitudDelDia) {
            const entradasDelDia = fichajesDelDia.filter(f => f.tipo === 'entrada');
            const salidasDelDia = fichajesDelDia.filter(f => f.tipo === 'salida');
            
            // Formatear todas las entradas y salidas
            const horasEntrada = entradasDelDia.map(f => formatearHora(new Date(f.fecha))).join(', ');
            const horasSalida = salidasDelDia.map(f => formatearHora(new Date(f.fecha))).join(', ');
            
            // Calcular tiempo total de jornada
            let tiempoJornada = '';
            if (entradasDelDia.length > 0 && salidasDelDia.length > 0) {
              let tiempoTotal = 0;
              const minLength = Math.min(entradasDelDia.length, salidasDelDia.length);
              for (let i = 0; i < minLength; i++) {
                const duracion = (new Date(salidasDelDia[i].fecha) - new Date(entradasDelDia[i].fecha)) / (1000 * 60 * 60);
                tiempoTotal += duracion;
              }
              tiempoJornada = `${tiempoTotal.toFixed(2)} horas`;
            }

            const fila = {
              'Nombre Completo': `${emp.nombre} ${emp.primerApellido || emp.apellidos || ''}`.trim(),
              'Fecha': fechaDisplay,
              'Hora Entrada': horasEntrada || '',
              'Hora Salida': horasSalida || '',
              'Tiempo de Jornada': tiempoJornada,
              'Vacaciones/Ausencia': solicitudDelDia 
                ? `${solicitudDelDia.tipo === 'vacaciones' ? 'Vacaciones' : 'Ausencia'}${solicitudDelDia.motivo ? ' - ' + solicitudDelDia.motivo : ''}` 
                : '-'
            };

            filas.push(fila);
          }
        }
      }

      if (filas.length === 0) {
        setLoadingExport(false);
        return alert("No hay registros con datos para exportar.");
      }
      
      // Crear hoja de fichajes
      const worksheet = XLSX.utils.json_to_sheet(filas);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Fichajes");
      
      // Obtener y agregar hoja de modificaciones
      try {
        // Formatear fechas en formato YYYY-MM-DD local (sin conversión UTC)
        const desde = `${fechaInicioBusqueda.getFullYear()}-${String(fechaInicioBusqueda.getMonth() + 1).padStart(2, '0')}-${String(fechaInicioBusqueda.getDate()).padStart(2, '0')}`;
        const hasta = `${fechaFinBusqueda.getFullYear()}-${String(fechaFinBusqueda.getMonth() + 1).padStart(2, '0')}-${String(fechaFinBusqueda.getDate()).padStart(2, '0')}`;
        
        const modificaciones = await getTodasModificaciones(token, { desde, hasta });
        
        if (modificaciones && modificaciones.length > 0) {
          // Consolidar modificaciones de crear/eliminar para evitar duplicados
          const modificacionesConsolidadas = [];
          const procesadas = new Set();
          
          modificaciones.forEach(mod => {
            // Si es EDITAR, siempre la mostramos (una línea por campo editado)
            if (mod.tipoModificacion === 'editar') {
              modificacionesConsolidadas.push(mod);
              return;
            }
            
            // Para CREAR y ELIMINAR, consolidamos tipo + fecha en una sola línea
            const fichajeIdStr = String(mod.fichajeId?._id || mod.fichajeId);
            const clave = `${mod.tipoModificacion}-${fichajeIdStr}`;
            
            if (procesadas.has(clave)) {
              return; // Ya procesada
            }
            procesadas.add(clave);
            
            // Buscar la modificación de fecha correspondiente
            const modFecha = modificaciones.find(m => 
              m.tipoModificacion === mod.tipoModificacion &&
              String(m.fichajeId?._id || m.fichajeId) === fichajeIdStr &&
              m.campoModificado === 'fecha'
            );
            
            // Buscar la modificación de tipo correspondiente
            const modTipo = modificaciones.find(m => 
              m.tipoModificacion === mod.tipoModificacion &&
              String(m.fichajeId?._id || m.fichajeId) === fichajeIdStr &&
              m.campoModificado === 'tipo'
            );
            
            // Usar la primera que encontramos pero con datos consolidados
            const modConsolidada = { ...mod };
            if (modFecha) {
              modConsolidada._fecha = modFecha;
            }
            if (modTipo) {
              modConsolidada._tipo = modTipo;
            }
            
            modificacionesConsolidadas.push(modConsolidada);
          });
          
          const filasModificaciones = modificacionesConsolidadas.map(mod => {
            // Función para formatear valores - SOLO HORA
            const formatearValor = (valor) => {
              if (!valor || valor === null) return '-';
              
              // Si es un objeto con fecha (caso antiguo que no debería pasar)
              if (typeof valor === 'object' && !(valor instanceof Date)) {
                if (valor.fecha) {
                  const fecha = new Date(valor.fecha);
                  return fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                }
                return '-';
              }
              
              // Si es una fecha ISO string o Date object
              if (valor instanceof Date || (typeof valor === 'string' && valor.includes('T'))) {
                const fecha = new Date(valor);
                return fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
              }
              
              // Si es entrada/salida como string
              if (valor === 'entrada' || valor === 'salida') {
                return '-';
              }
              
              return String(valor);
            };

            // Determinar nombre del campo y valores según el tipo de modificación
            let nombreCampo = '';
            let valorAnterior = mod.valorAnterior;
            let valorNuevo = mod.valorNuevo;
            
            // Para modificaciones consolidadas (CREAR/ELIMINAR)
            if (mod._tipo || mod._fecha) {
              const modTipo = mod._tipo || mod;
              const modFecha = mod._fecha || mod;
              
              // El campo es siempre Entrada o Salida
              const tipoFichaje = modTipo.valorAnterior || modTipo.valorNuevo;
              nombreCampo = tipoFichaje === 'entrada' ? 'Entrada' : 'Salida';
              
              // Los valores son las horas (de la modificación de fecha)
              valorAnterior = modFecha.valorAnterior;
              valorNuevo = modFecha.valorNuevo;
            } 
            // Para modificaciones de edición
            else if (mod.campoModificado === 'tipo') {
              const tipoFichaje = mod.valorAnterior || mod.valorNuevo;
              nombreCampo = tipoFichaje === 'entrada' ? 'Entrada' : 'Salida';
            } else if (mod.campoModificado === 'fecha') {
              // Para modificaciones de fecha, intentar obtener el tipo del fichaje
              if (mod.fichajeId && mod.fichajeId.tipo) {
                nombreCampo = mod.fichajeId.tipo === 'entrada' ? 'Entrada' : 'Salida';
              } else {
                // Si no hay fichajeId.tipo, buscar en la misma lista de modificaciones
                const modTipo = modificacionesConsolidadas.find(m => 
                  m.fichajeId && mod.fichajeId && 
                  String(m.fichajeId._id || m.fichajeId) === String(mod.fichajeId._id || mod.fichajeId) && 
                  m.campoModificado === 'tipo'
                );
                if (modTipo) {
                  const tipoFichaje = modTipo.valorAnterior || modTipo.valorNuevo;
                  nombreCampo = tipoFichaje === 'entrada' ? 'Entrada' : 'Salida';
                } else {
                  nombreCampo = '-';
                }
              }
            }

            // Obtener fecha del fichaje
            let fechaFichaje = '-';
            if (mod.fichajeId && mod.fichajeId.fecha) {
              fechaFichaje = new Date(mod.fichajeId.fecha).toLocaleDateString('es-ES');
            }

            return {
              'Empleado': mod.empleado ? `${mod.empleado.nombre} ${mod.empleado.primerApellido || ''} ${mod.empleado.segundoApellido || ''}`.trim() : 'Desconocido',
              'Fecha Fichaje': fechaFichaje,
              'Fecha Modificación': new Date(mod.fechaModificacion).toLocaleString('es-ES'),
              'Acción': mod.tipoModificacion === 'crear' ? 'CREADO' : mod.tipoModificacion === 'editar' ? 'EDITADO' : 'ELIMINADO',
              'Campo': nombreCampo,
              'Valor Anterior': formatearValor(valorAnterior),
              'Valor Nuevo': formatearValor(valorNuevo),
              'Modificado Por': mod.modificadoPor ? `${mod.modificadoPor.nombre} ${mod.modificadoPor.primerApellido || ''}`.trim() : 'Desconocido',
              'Motivo': mod.motivo || '-'
            };
          });
          
          const worksheetModificaciones = XLSX.utils.json_to_sheet(filasModificaciones);
          XLSX.utils.book_append_sheet(workbook, worksheetModificaciones, "Modificaciones");
        }
      } catch (error) {
        // Continuar con la exportación aunque falle obtener modificaciones
      }
      
      const fechaInicioStr = formatearFecha(fechaInicioBusqueda).replace(/\//g, '-');
      const fechaFinStr = formatearFecha(fechaFinBusqueda).replace(/\//g, '-');
      const nombreArchivo = `fichajes_${fechaInicioStr}_a_${fechaFinStr}.xlsx`;
      
      XLSX.writeFile(workbook, nombreArchivo);
    } catch (error) {
      alert('Error al generar el archivo Excel');
    } finally {
      setLoadingExport(false);
    }
  };

  // Función para exportar a PDF
  const exportarPDF = async () => {
    setLoadingExport(true);
    
    // Yield mínimo para que el modal se muestre
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      if (fichajes.length === 0 && solicitudes.length === 0) {
        setLoadingExport(false);
        return alert("No hay datos para exportar.");
      }

      const filas = [];
      
      // Procesamiento síncrono - SIN yields
      for (const emp of empleadosFiltrados) {
        for (const fecha of fechas) {
          const fechaStr = toLocalDateStr(fecha);
          const fechaDisplay = formatearFecha(fecha);
          
          // Buscar fichajes del empleado en esa fecha
          const fichajesDelDia = fichajes.filter(f => {
            const fFecha = toLocalDateStr(f.fecha);
            const userId = f.userId?._id || f.userId;
            return String(userId) === String(emp._id) && fFecha === fechaStr;
          });
          
          // Buscar solicitud de ese día
          const solicitudDelDia = solicitudes.find(s => {
            const inicio = toLocalDateStr(s.fechaInicio);
            const fin = toLocalDateStr(s.fechaFin);
            const usuarioId = s.usuario?._id || s.usuario;
            return String(usuarioId) === String(emp._id) && fechaStr >= inicio && fechaStr <= fin;
          });

          // Solo añadir fila si hay datos (fichajes o solicitud)
          if (fichajesDelDia.length > 0 || solicitudDelDia) {
            const entradasDelDia = fichajesDelDia.filter(f => f.tipo === 'entrada');
            const salidasDelDia = fichajesDelDia.filter(f => f.tipo === 'salida');
            
            // Formatear todas las entradas y salidas
            const horasEntrada = entradasDelDia.map(f => formatearHora(new Date(f.fecha))).join(', ');
            const horasSalida = salidasDelDia.map(f => formatearHora(new Date(f.fecha))).join(', ');
            
            // Calcular tiempo total de jornada
            let tiempoJornada = '';
            if (entradasDelDia.length > 0 && salidasDelDia.length > 0) {
              let tiempoTotal = 0;
              const minLength = Math.min(entradasDelDia.length, salidasDelDia.length);
              for (let i = 0; i < minLength; i++) {
                const duracion = (new Date(salidasDelDia[i].fecha) - new Date(entradasDelDia[i].fecha)) / (1000 * 60 * 60);
                tiempoTotal += duracion;
              }
              tiempoJornada = `${tiempoTotal.toFixed(2)}h`;
            }

            const vacacionAusencia = solicitudDelDia 
              ? `${solicitudDelDia.tipo === 'vacaciones' ? 'Vacaciones' : 'Ausencia'}${solicitudDelDia.motivo ? ' - ' + solicitudDelDia.motivo : ''}` 
              : '-';

            filas.push([
              `${emp.nombre} ${emp.primerApellido || emp.apellidos || ''}`.trim(),
              fechaDisplay,
              horasEntrada || '-',
              horasSalida || '-',
              tiempoJornada || '-',
              vacacionAusencia
            ]);
          }
        }
      }

      if (filas.length === 0) {
        setLoadingExport(false);
        return alert("No hay registros con datos para exportar.");
      }

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        compress: true // HABILITAR COMPRESIÓN
      });
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const fechaInicioStr = formatearFecha(fechaInicioBusqueda);
      const fechaFinStr = formatearFecha(fechaFinBusqueda);
      
      // NO AGREGAR LOGO - Reduce significativamente el tamaño del PDF
      // Si necesitas logo, optimízalo primero a menos de 50KB
      
      const empresaNombre = user?.empresa?.nombre || user?.empresa || 'Empresa';
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      const empresaWidth = doc.getTextWidth(empresaNombre);
      doc.text(empresaNombre, pageWidth - empresaWidth - 14, 15);
      
      doc.setFontSize(14);
      const titulo = `Reporte de Fichajes - ${fechaInicioStr} a ${fechaFinStr}`;
      const tituloWidth = doc.getTextWidth(titulo);
      doc.text(titulo, (pageWidth - tituloWidth) / 2, 20);
      
      // Tabla SIMPLIFICADA - sin estilos pesados
      autoTable(doc, {
        startY: 25,
        head: [['Nombre', 'Fecha', 'Entrada', 'Salida', 'Tiempo', 'Vac/Aus']],
        body: filas,
        theme: 'plain', // Tema simple en vez de 'grid'
        styles: {
          fontSize: 7,
          cellPadding: 1,
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [79, 70, 229],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8
        },
        margin: { top: 25, left: 10, right: 10 },
        didDrawPage: function(data) {
          // Header simple en cada página (sin logo)
          doc.setFontSize(10);
          doc.setTextColor(100);
          doc.text(`Página ${doc.internal.getCurrentPageInfo().pageNumber}`, pageWidth - 30, 10);
        }
      });
      
      // Obtener y agregar tabla de modificaciones
      try {
        // Formatear fechas en formato YYYY-MM-DD local (sin conversión UTC)
        const desde = `${fechaInicioBusqueda.getFullYear()}-${String(fechaInicioBusqueda.getMonth() + 1).padStart(2, '0')}-${String(fechaInicioBusqueda.getDate()).padStart(2, '0')}`;
        const hasta = `${fechaFinBusqueda.getFullYear()}-${String(fechaFinBusqueda.getMonth() + 1).padStart(2, '0')}-${String(fechaFinBusqueda.getDate()).padStart(2, '0')}`;
        
        const modificaciones = await getTodasModificaciones(token, { desde, hasta });
        
        if (modificaciones && modificaciones.length > 0) {
          // Consolidar modificaciones de crear/eliminar para evitar duplicados
          const modificacionesConsolidadas = [];
          const procesadas = new Set();
          
          modificaciones.forEach(mod => {
            // Si es EDITAR, siempre la mostramos (una línea por campo editado)
            if (mod.tipoModificacion === 'editar') {
              modificacionesConsolidadas.push(mod);
              return;
            }
            
            // Para CREAR y ELIMINAR, consolidamos tipo + fecha en una sola línea
            const fichajeIdStr = String(mod.fichajeId?._id || mod.fichajeId);
            const clave = `${mod.tipoModificacion}-${fichajeIdStr}`;
            
            if (procesadas.has(clave)) {
              return; // Ya procesada
            }
            procesadas.add(clave);
            
            // Buscar la modificación de fecha correspondiente
            const modFecha = modificaciones.find(m => 
              m.tipoModificacion === mod.tipoModificacion &&
              String(m.fichajeId?._id || m.fichajeId) === fichajeIdStr &&
              m.campoModificado === 'fecha'
            );
            
            // Buscar la modificación de tipo correspondiente
            const modTipo = modificaciones.find(m => 
              m.tipoModificacion === mod.tipoModificacion &&
              String(m.fichajeId?._id || m.fichajeId) === fichajeIdStr &&
              m.campoModificado === 'tipo'
            );
            
            // Usar la primera que encontramos pero con datos consolidados
            const modConsolidada = { ...mod };
            if (modFecha) {
              modConsolidada._fecha = modFecha;
            }
            if (modTipo) {
              modConsolidada._tipo = modTipo;
            }
            
            modificacionesConsolidadas.push(modConsolidada);
          });
          
          const filasModificaciones = modificacionesConsolidadas.map(mod => {
            // Función para formatear valores - SOLO HORA
            const formatearValor = (valor) => {
              if (!valor || valor === null) return '-';
              
              // Si es un objeto con fecha (caso antiguo que no debería pasar)
              if (typeof valor === 'object' && !(valor instanceof Date)) {
                if (valor.fecha) {
                  const fecha = new Date(valor.fecha);
                  return fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                }
                return '-';
              }
              
              // Si es una fecha ISO string o Date object
              if (valor instanceof Date || (typeof valor === 'string' && valor.includes('T'))) {
                const fecha = new Date(valor);
                return fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
              }
              
              // Si es entrada/salida como string
              if (valor === 'entrada' || valor === 'salida') {
                return '-';
              }
              
              return String(valor);
            };

            // Determinar nombre del campo y valores según el tipo de modificación
            let nombreCampo = '';
            let valorAnterior = mod.valorAnterior;
            let valorNuevo = mod.valorNuevo;
            
            // Para modificaciones consolidadas (CREAR/ELIMINAR)
            if (mod._tipo || mod._fecha) {
              const modTipo = mod._tipo || mod;
              const modFecha = mod._fecha || mod;
              
              // El campo es siempre Entrada o Salida
              const tipoFichaje = modTipo.valorAnterior || modTipo.valorNuevo;
              nombreCampo = tipoFichaje === 'entrada' ? 'Entrada' : 'Salida';
              
              // Los valores son las horas (de la modificación de fecha)
              valorAnterior = modFecha.valorAnterior;
              valorNuevo = modFecha.valorNuevo;
            } 
            // Para modificaciones de edición
            else if (mod.campoModificado === 'tipo') {
              const tipoFichaje = mod.valorAnterior || mod.valorNuevo;
              nombreCampo = tipoFichaje === 'entrada' ? 'Entrada' : 'Salida';
            } else if (mod.campoModificado === 'fecha') {
              // Para modificaciones de fecha, intentar obtener el tipo del fichaje
              if (mod.fichajeId && mod.fichajeId.tipo) {
                nombreCampo = mod.fichajeId.tipo === 'entrada' ? 'Entrada' : 'Salida';
              } else {
                // Si no hay fichajeId.tipo, buscar en la misma lista de modificaciones
                const modTipo = modificacionesConsolidadas.find(m => 
                  m.fichajeId && mod.fichajeId && 
                  String(m.fichajeId._id || m.fichajeId) === String(mod.fichajeId._id || mod.fichajeId) && 
                  m.campoModificado === 'tipo'
                );
                if (modTipo) {
                  const tipoFichaje = modTipo.valorAnterior || modTipo.valorNuevo;
                  nombreCampo = tipoFichaje === 'entrada' ? 'Entrada' : 'Salida';
                } else {
                  nombreCampo = '-';
                }
              }
            }

            // Obtener fecha del fichaje
            let fechaFichaje = '-';
            if (mod.fichajeId && mod.fichajeId.fecha) {
              fechaFichaje = new Date(mod.fichajeId.fecha).toLocaleDateString('es-ES');
            }

            return [
              mod.empleado ? `${mod.empleado.nombre} ${mod.empleado.primerApellido || ''}`.trim() : 'Desconocido',
              fechaFichaje,
              new Date(mod.fechaModificacion).toLocaleString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }),
              mod.tipoModificacion === 'crear' ? 'CREADO' : mod.tipoModificacion === 'editar' ? 'EDITADO' : 'ELIMINADO',
              nombreCampo,
              formatearValor(valorAnterior),
              formatearValor(valorNuevo),
              mod.modificadoPor ? `${mod.modificadoPor.nombre} ${mod.modificadoPor.primerApellido || ''}`.trim() : 'Desconocido',
              mod.motivo || '-'
            ];
          });
          
          // Añadir título de sección
          const finalY = doc.lastAutoTable.finalY;
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Historial de Modificaciones', 14, finalY + 10);
          
          // Agregar tabla de modificaciones OPTIMIZADA
          autoTable(doc, {
            startY: finalY + 15,
            head: [['Empleado', 'Fecha Fichaje', 'Fecha Mod.', 'Acción', 'Campo', 'Ant.', 'Nuevo', 'Por', 'Motivo']],
            body: filasModificaciones,
            theme: 'plain',
            styles: {
              fontSize: 5,
              cellPadding: 0.5,
              lineColor: [220, 220, 220],
              lineWidth: 0.1
            },
            headStyles: {
              fillColor: [59, 130, 246],
              textColor: 255,
              fontStyle: 'bold',
              fontSize: 5
            },
            columnStyles: {
              0: { cellWidth: 25 }, // Empleado
              1: { cellWidth: 20 }, // Fecha Fichaje
              2: { cellWidth: 25 }, // Fecha Mod
              3: { cellWidth: 15 }, // Acción
              4: { cellWidth: 15 }, // Campo
              5: { cellWidth: 15 }, // Ant
              6: { cellWidth: 15 }, // Nuevo
              7: { cellWidth: 20 }, // Por
              8: { cellWidth: 'auto' } // Motivo
            },
            margin: { left: 5, right: 5 }
          });
        }
      } catch (error) {
        // Continuar con la exportación aunque falle obtener modificaciones
      }
      
      const nombreArchivo = `fichajes_${fechaInicioStr.replace(/\//g, '-')}_a_${fechaFinStr.replace(/\//g, '-')}.pdf`;
      doc.save(nombreArchivo);
      
      // Cerrar modal después de guardar
      setLoadingExport(false);
    } catch (error) {
      alert('Error al generar el archivo PDF');
      setLoadingExport(false);
    }
  };

  // Función para generar informes mensuales de firma (PDFs individuales)
  const generarInformesFirma = async () => {
    if (empleadosFirma.length === 0) {
      alert("Selecciona al menos un trabajador para generar el informe.");
      return;
    }

    setLoadingFirma(true);
    
    try {
      // Obtener datos actualizados de la empresa (incluyendo CIF)
      const empresaId = user?.empresa?._id || user?.empresa;
      let empresaCompleta = user?.empresa;
      
      if (empresaId) {
        try {
          const empresasResponse = await fetch(`${BACKEND_URL}/api/v1/empresas`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const empresas = await empresasResponse.json();
          empresaCompleta = empresas.find(e => e._id === String(empresaId));
        } catch (err) {
        }
      }
      
      // Calcular rango de fechas del mes seleccionado
      const primerDia = new Date(anioFirma, mesFirma - 1, 1);
      const ultimoDia = new Date(anioFirma, mesFirma, 0);
      
      // Formatear fechas para la API
      const desde = `${anioFirma}-${String(mesFirma).padStart(2, '0')}-01`;
      const hasta = `${anioFirma}-${String(mesFirma).padStart(2, '0')}-${String(ultimoDia.getDate()).padStart(2, '0')}`;
      
      // Obtener fichajes y modificaciones del mes
      const query = `?from=${desde}&to=${hasta}`;
      const fichajesDelMes = await getFichajes(token, query);
      
      // Convertir a array si es un objeto pseudo-array
      let fichajesArray;
      if (Array.isArray(fichajesDelMes)) {
        fichajesArray = fichajesDelMes;
      } else if (fichajesDelMes && typeof fichajesDelMes === 'object') {
        const keys = Object.keys(fichajesDelMes).filter(k => !isNaN(parseInt(k)));
        fichajesArray = keys.map(k => fichajesDelMes[k]);
      } else {
        fichajesArray = [];
      }
      
      const modificacionesDelMes = await getTodasModificaciones(token, { desde, hasta });
      
      // Generar un PDF por cada empleado seleccionado
      for (const empId of empleadosFirma) {
        const empleado = empleados.find(e => e._id === empId);
        if (!empleado) continue;
        
        // Filtrar fichajes del empleado
        const fichajesEmpleado = fichajesArray.filter(f => {
          const userId = f.userId?._id || f.userId;
          return String(userId) === String(empId);
        });
        
        // Filtrar modificaciones del empleado
        const modificacionesEmpleado = modificacionesDelMes.filter(m => {
          const empIdMod = m.empleado?._id || m.empleado;
          return String(empIdMod) === String(empId);
        });
        
        // Crear PDF
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true
        });
        
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        
        // CABECERA - Formulario con datos de empresa y trabajador
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        const titulo = `Informe de Fichajes - ${getNombreMes(mesFirma)} ${anioFirma}`;
        const tituloWidth = doc.getTextWidth(titulo);
        doc.text(titulo, (pageWidth - tituloWidth) / 2, 15);
        
        // Recuadro de información
        doc.setDrawColor(100, 100, 100);
        doc.setLineWidth(0.5);
        doc.rect(14, 25, pageWidth - 28, 35);
        
        // Línea vertical divisoria
        doc.line(pageWidth / 2, 25, pageWidth / 2, 60);
        
        // IZQUIERDA - Datos de la empresa
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('EMPRESA:', 18, 32);
        doc.setFont('helvetica', 'normal');
        const empresaNombre = empresaCompleta?.nombre || user?.empresa?.nombre || 'Empresa';
        doc.text(empresaNombre, 18, 38);
        
        doc.setFont('helvetica', 'bold');
        doc.text('C.I.F/N.I.F:', 18, 45);
        doc.setFont('helvetica', 'normal');
        const empresaCIF = empresaCompleta?.cif || user?.empresa?.cif || '-';
        doc.text(empresaCIF, 18, 51);
        
        // DERECHA - Datos del trabajador
        doc.setFont('helvetica', 'bold');
        doc.text('TRABAJADOR:', pageWidth / 2 + 4, 32);
        doc.setFont('helvetica', 'normal');
        const nombreCompleto = `${empleado.nombre} ${empleado.primerApellido || ''} ${empleado.segundoApellido || ''}`.trim();
        doc.text(nombreCompleto, pageWidth / 2 + 4, 38);
        
        doc.setFont('helvetica', 'bold');
        doc.text('N.I.F:', pageWidth / 2 + 4, 45);
        doc.setFont('helvetica', 'normal');
        const trabajadorNIF = empleado.nif || empleado.dni || '-';
        doc.text(trabajadorNIF, pageWidth / 2 + 4, 51);
        
        // Preparar datos de fichajes
        const filasFichajes = [];
        
        // Agrupar fichajes por fecha
        const fichajesPorFecha = {};
        fichajesEmpleado.forEach(f => {
          const fechaStr = toLocalDateStr(f.fecha);
          if (!fichajesPorFecha[fechaStr]) {
            fichajesPorFecha[fechaStr] = { entradas: [], salidas: [] };
          }
          if (f.tipo === 'entrada') {
            fichajesPorFecha[fechaStr].entradas.push(f);
          } else {
            fichajesPorFecha[fechaStr].salidas.push(f);
          }
        });
        
        // Crear filas de la tabla
        Object.keys(fichajesPorFecha).sort().forEach(fechaStr => {
          const fecha = new Date(fechaStr);
          const fechaDisplay = fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const diaSemana = fecha.toLocaleDateString('es-ES', { weekday: 'short' });
          
          const { entradas, salidas } = fichajesPorFecha[fechaStr];
          const horasEntrada = entradas.map(f => formatearHora(new Date(f.fecha))).join(', ');
          const horasSalida = salidas.map(f => formatearHora(new Date(f.fecha))).join(', ');
          
          // Calcular tiempo total
          let tiempoTotal = 0;
          const minLength = Math.min(entradas.length, salidas.length);
          for (let i = 0; i < minLength; i++) {
            const duracion = (new Date(salidas[i].fecha) - new Date(entradas[i].fecha)) / (1000 * 60 * 60);
            tiempoTotal += duracion;
          }
          const tiempoDisplay = tiempoTotal > 0 ? `${tiempoTotal.toFixed(2)}h` : '-';
          
          filasFichajes.push([
            `${diaSemana}`,
            fechaDisplay,
            horasEntrada || '-',
            horasSalida || '-',
            tiempoDisplay
          ]);
        });
        
        // Tabla de fichajes
        autoTable(doc, {
          startY: 68,
          head: [['Día', 'Fecha', 'Entrada', 'Salida', 'Total']],
          body: filasFichajes.length > 0 ? filasFichajes : [['Sin fichajes registrados', '', '', '', '']],
          theme: 'grid',
          styles: {
            fontSize: 9,
            cellPadding: 3
          },
          headStyles: {
            fillColor: [79, 70, 229],
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'
          },
          columnStyles: {
            0: { cellWidth: 20, halign: 'center' },
            1: { cellWidth: 30, halign: 'center' },
            2: { cellWidth: 40, halign: 'center' },
            3: { cellWidth: 40, halign: 'center' },
            4: { cellWidth: 25, halign: 'center' }
          },
          margin: { left: 14, right: 14 }
        });
        
        // Tabla de modificaciones (si las hay)
        if (modificacionesEmpleado.length > 0) {
          const finalY = doc.lastAutoTable.finalY + 10;
          
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text('Historial de Modificaciones', 14, finalY);
          
          const filasModificaciones = modificacionesEmpleado.map(mod => {
            const fechaFichaje = mod.fichajeId?.fecha 
              ? new Date(mod.fichajeId.fecha).toLocaleDateString('es-ES')
              : '-';
            
            const fechaMod = new Date(mod.fechaModificacion).toLocaleString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
            
            const accion = mod.tipoModificacion === 'crear' ? 'CREADO' : 
                          mod.tipoModificacion === 'editar' ? 'EDITADO' : 'ELIMINADO';
            
            const modificadoPor = mod.modificadoPor 
              ? `${mod.modificadoPor.nombre} ${mod.modificadoPor.primerApellido || ''}`.trim()
              : 'Desconocido';
            
            return [
              fechaFichaje,
              fechaMod,
              accion,
              modificadoPor,
              mod.motivo || '-'
            ];
          });
          
          autoTable(doc, {
            startY: finalY + 5,
            head: [['Fecha Fichaje', 'Fecha Mod.', 'Acción', 'Por', 'Motivo']],
            body: filasModificaciones,
            theme: 'grid',
            styles: {
              fontSize: 7,
              cellPadding: 2
            },
            headStyles: {
              fillColor: [59, 130, 246],
              textColor: 255,
              fontStyle: 'bold',
              fontSize: 8
            },
            columnStyles: {
              0: { cellWidth: 25 },
              1: { cellWidth: 35 },
              2: { cellWidth: 20 },
              3: { cellWidth: 35 },
              4: { cellWidth: 'auto' }
            },
            margin: { left: 14, right: 14 }
          });
        }
        
        // SECCIÓN DE FIRMAS al final de la página
        const firmasY = pageHeight - 50;
        
        // Recuadro para firmas
        doc.setDrawColor(100, 100, 100);
        doc.setLineWidth(0.3);
        
        // Firma EMPRESA (izquierda)
        doc.rect(14, firmasY, 80, 40);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('FIRMA EMPRESA', 54, firmasY + 5, { align: 'center' });
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text('Fecha: ___/___/______', 54, firmasY + 35, { align: 'center' });
        
        // Firma TRABAJADOR (derecha)
        doc.rect(pageWidth - 94, firmasY, 80, 40);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('FIRMA TRABAJADOR', pageWidth - 54, firmasY + 5, { align: 'center' });
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text('Fecha: ___/___/______', pageWidth - 54, firmasY + 35, { align: 'center' });
        
        // Guardar PDF con nombre específico
        const nombreMes = getNombreMes(mesFirma);
        const apellido = empleado.primerApellido || empleado.apellidos || '';
        const nombreArchivo = `${empleado.nombre}${apellido ? '_' + apellido : ''}_${nombreMes}_${anioFirma}.pdf`;
        doc.save(nombreArchivo);
        
        // Pequeña pausa entre PDFs para evitar problemas de descarga
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      setLoadingFirma(false);
      alert(`Se han generado ${empleadosFirma.length} informe(s) de firma correctamente.`);
      
    } catch (error) {
      alert('Error al generar los informes de firma');
      setLoadingFirma(false);
    }
  };
  
  // Función auxiliar para obtener nombre del mes
  const getNombreMes = (mes) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return meses[mes - 1];
  };

  // Preparar datos para vista de tabla - agrupados por empleado y fecha
  const datosTabla = useMemo(() => {
    const agrupado = {};
    
    // Validar que hay fechas de búsqueda
    if (!fechaInicioBusqueda || !fechaFinBusqueda) return [];
    
    // Crear objetos Date para comparación (sin hora)
    const fechaBusquedaInicio = new Date(fechaInicioBusqueda);
    fechaBusquedaInicio.setHours(0, 0, 0, 0);
    const fechaBusquedaFin = new Date(fechaFinBusqueda);
    fechaBusquedaFin.setHours(23, 59, 59, 999);
    
    // Agrupar fichajes por empleado y fecha (solo empleados seleccionados y en rango de fechas)
    fichajes.forEach((f) => {
      const userId = f.userId?._id;
      
      // Filtrar por empleados seleccionados
      if (!empleadosSeleccionados.includes(userId)) return;
      
      // Filtrar por rango de fechas
      const fechaFichaje = new Date(f.fecha);
      fechaFichaje.setHours(0, 0, 0, 0);
      if (fechaFichaje < fechaBusquedaInicio || fechaFichaje > fechaBusquedaFin) return;
      
      const dateKey = new Date(f.fecha).toLocaleDateString("es-ES");
      const key = `${userId}-${dateKey}`;

      if (!agrupado[key]) {
        agrupado[key] = {
          userId: f.userId,
          fecha: f.fecha,
          registros: [],
          solicitud: null
        };
      }

      agrupado[key].registros.push(f);
    });

    // Agregar solicitudes (vacaciones/ausencias) como registros (solo empleados seleccionados y en rango de fechas)
    solicitudes.forEach((s) => {
      const userId = s.usuario?._id || s.usuario;
      
      // Filtrar por empleados seleccionados
      if (!empleadosSeleccionados.includes(userId)) return;
      
      const inicio = new Date(s.fechaInicio);
      const fin = new Date(s.fechaFin);
      
      // Filtrar por rango de fechas - solo procesar días que estén dentro del rango de búsqueda
      const inicioAjustado = new Date(Math.max(inicio, fechaBusquedaInicio));
      const finAjustado = new Date(Math.min(fin, fechaBusquedaFin));
      
      // Si la solicitud no intersecta con el rango de búsqueda, saltar
      if (inicioAjustado > finAjustado) return;
      
      const current = new Date(inicioAjustado);
      
      // Iterar por cada día de la solicitud dentro del rango
      while (current <= finAjustado) {
        const dateKey = current.toLocaleDateString("es-ES");
        const key = `${userId}-${dateKey}`;
        
        // Buscar el usuario en la lista de empleados para obtener datos completos
        const usuario = empleados.find(e => String(e._id) === String(userId));
        
        if (!agrupado[key]) {
          agrupado[key] = {
            userId: usuario || { _id: userId, nombre: 'Desconocido', apellidos: '' },
            fecha: new Date(current),
            registros: [],
            solicitud: s
          };
        } else {
          agrupado[key].solicitud = s;
        }
        
        current.setDate(current.getDate() + 1);
      }
    });

    // Ordenar registros dentro de cada día por hora
    Object.values(agrupado).forEach((item) => {
      item.registros.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
    });

    return Object.values(agrupado).sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [fichajes, solicitudes, empleados, empleadosSeleccionados, fechaInicioBusqueda, fechaFinBusqueda]);
  
  const getIconoYColor = (tipo) => {
    switch (tipo) {
      case "entrada":
        return { icono: <i className="fas fa-sign-in-alt"></i>, color: "bg-green-100 text-green-700 border border-green-300", label: "Entrada" };
      case "salida":
        return { icono: <i className="fas fa-sign-out-alt"></i>, color: "bg-red-100 text-red-700 border border-red-300", label: "Salida" };
      case "ausencia":
        return { icono: <i className="fas fa-ban"></i>, color: "bg-gray-100 text-gray-700 border border-gray-300", label: "Ausencia" };
      default:
        return { icono: <i className="fas fa-question"></i>, color: "bg-yellow-100 text-yellow-700 border border-yellow-300", label: tipo };
    }
  };
  
  // Paginación para vista tabla
  const totalPaginas = Math.ceil(datosTabla.length / itemsPorPagina);
  const inicio = (paginaActual - 1) * itemsPorPagina;
  const fin = inicio + itemsPorPagina;
  const datosPaginados = datosTabla.slice(inicio, fin);

  const generarNumeroPaginas = () => {
    const nums = [];
    const rango = 5;

    let start = Math.max(1, paginaActual - rango);
    let end = Math.min(totalPaginas, paginaActual + rango);

    if (paginaActual <= rango) {
      end = Math.min(totalPaginas, 2 * rango + 1);
    } else if (paginaActual > totalPaginas - rango) {
      start = Math.max(1, totalPaginas - 2 * rango);
    }

    for (let i = start; i <= end; i++) {
      nums.push(i);
    }
    return nums;
  };

  const fechas_old = generarFechas();
  const empleadosFiltrados_old = empleados.filter(e => empleadosSeleccionados.includes(e._id));

  return (
    <div className="bg-white rounded-lg shadow-md p-6 overflow-hidden">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            <i className="fas fa-calendar-alt mr-2"></i>
            Control de Horarios
          </h2>
          <p className="text-gray-600 text-sm">
            Vista general de fichajes y ausencias de empleados
          </p>
        </div>
        
        {/* Botones de acción */}
        <div className="flex gap-2">
          {/* Toggle de vista */}
          <div className="bg-gray-100 rounded-lg p-1 flex gap-1">
            <button
              onClick={() => setVistaActual("calendario")}
              className={`px-3 py-2 rounded-md text-sm font-medium transition ${
                vistaActual === "calendario"
                  ? "bg-white text-gray-800 shadow"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <i className="fas fa-calendar mr-1"></i>
              Calendario
            </button>
            <button
              onClick={() => setVistaActual("tabla")}
              className={`px-3 py-2 rounded-md text-sm font-medium transition ${
                vistaActual === "tabla"
                  ? "bg-white text-gray-800 shadow"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <i className="fas fa-table mr-1"></i>
              Tabla
            </button>
          </div>
          
          {/* Botones de exportar */}
          <div className="flex gap-2 flex-wrap">
            {/* Botón crear fichaje manual (solo en vista tabla) */}
            {vistaActual === "tabla" && (
              <button
                onClick={() => setModalCrearFichaje(true)}
                className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition flex items-center gap-2 text-sm font-medium"
              >
                <i className="fas fa-plus"></i>
                Crear Fichaje
              </button>
            )}
            
            <button
              onClick={exportarExcel}
              disabled={loadingExport}
              className={`${
                loadingExport 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-500 hover:bg-green-600'
              } text-white px-4 py-2 rounded-lg transition flex items-center gap-2 text-sm font-medium`}
            >
              {loadingExport ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Exportando...
                </>
              ) : (
                <>
                  <i className="fas fa-file-excel"></i>
                  Excel
                </>
              )}
            </button>
            <button
              onClick={exportarPDF}
              disabled={loadingExport}
              className={`${
                loadingExport 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-red-500 hover:bg-red-600'
              } text-white px-4 py-2 rounded-lg transition flex items-center gap-2 text-sm font-medium`}
            >
              {loadingExport ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Exportando...
                </>
              ) : (
                <>
                  <i className="fas fa-file-pdf"></i>
                  PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Nueva sección: Informes Mensuales de Firma - DESPLEGABLE */}
      <div className="mb-6 pb-6 border-b-2 border-gray-300">
        <button
          onClick={() => setMostrarSeccionFirma(!mostrarSeccionFirma)}
          className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 rounded-lg transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="bg-indigo-600 text-white w-10 h-10 rounded-lg flex items-center justify-center">
              <i className="fas fa-file-signature"></i>
            </div>
            <div className="text-left">
              <h3 className="text-lg font-bold text-gray-800">
                Informes Mensuales de Firma
              </h3>
              <p className="text-sm text-gray-600">
                Genera informes individuales para la firma mensual de fichajes
              </p>
            </div>
          </div>
          <i className={`fas fa-chevron-${mostrarSeccionFirma ? 'up' : 'down'} text-gray-500 text-xl transition-transform`}></i>
        </button>
        
        {mostrarSeccionFirma && (
          <div className="mt-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Selector de Mes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-calendar-days mr-1"></i>
                  Mes
                </label>
                <select
                  value={mesFirma}
                  onChange={(e) => setMesFirma(parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value={1}>Enero</option>
                  <option value={2}>Febrero</option>
                  <option value={3}>Marzo</option>
                  <option value={4}>Abril</option>
                  <option value={5}>Mayo</option>
                  <option value={6}>Junio</option>
                  <option value={7}>Julio</option>
                  <option value={8}>Agosto</option>
                  <option value={9}>Septiembre</option>
                  <option value={10}>Octubre</option>
                  <option value={11}>Noviembre</option>
                  <option value={12}>Diciembre</option>
                </select>
              </div>
              
              {/* Selector de Año */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-calendar mr-1"></i>
                  Año
                </label>
                <select
                  value={anioFirma}
                  onChange={(e) => setAnioFirma(parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              
              {/* Selector de Trabajadores con Checkboxes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-users mr-1"></i>
                  Trabajadores ({empleadosFirma.length}/{empleados.length})
                </label>
                <div className="relative">
                  <button
                    onClick={() => setShowEmpleadosFirmaDropdown(!showEmpleadosFirmaDropdown)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-left bg-white hover:bg-gray-50 transition flex items-center justify-between"
                  >
                    <span className="text-sm text-gray-700">
                      {empleadosFirma.length === 0
                        ? "Selecciona trabajadores"
                        : `${empleadosFirma.length} seleccionado${empleadosFirma.length !== 1 ? 's' : ''}`}
                    </span>
                    <i className={`fas fa-chevron-${showEmpleadosFirmaDropdown ? 'up' : 'down'} text-gray-500`}></i>
                  </button>

                  {showEmpleadosFirmaDropdown && (
                    <div className="absolute z-50 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                        <button
                          onClick={toggleTodosFirma}
                          className="w-full text-left px-3 py-2 hover:bg-indigo-50 rounded text-sm font-semibold text-gray-700 transition"
                        >
                          <i className={`fas fa-${empleadosFirma.length === empleados.length ? 'check-square' : 'square'} mr-2 text-indigo-600`}></i>
                          Todos
                        </button>
                      </div>
                      {empleados.map(emp => (
                        <label
                          key={emp._id}
                          className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer transition"
                        >
                          <input
                            type="checkbox"
                            checked={empleadosFirma.includes(emp._id)}
                            onChange={() => toggleEmpleadoFirma(emp._id)}
                            className="mr-3 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">{emp.nombre} {emp.primerApellido || emp.apellidos}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Botón Generar */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                <i className="fas fa-info-circle mr-1"></i>
                Se generará un PDF independiente por cada trabajador seleccionado
              </p>
              <button
                onClick={generarInformesFirma}
                disabled={loadingFirma || empleadosFirma.length === 0}
                className={`${
                  loadingFirma || empleadosFirma.length === 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 shadow-lg hover:shadow-xl'
                } text-white px-6 py-3 rounded-lg transition-all flex items-center gap-2 font-semibold`}
              >
                {loadingFirma ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Generando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-download"></i>
                    Generar Informes
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filtros de búsqueda de fichajes */}
      <div className="space-y-4 mb-6 pb-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <i className="fas fa-filter"></i>
          Filtros de Búsqueda
        </h3>
        {/* Selector de empleados */}
        <div className="relative">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            <i className="fas fa-users mr-1"></i>
            Empleados ({empleadosSeleccionados.length}/{empleados.length})
          </label>
          <button
            onClick={() => setShowEmpleadosDropdown(!showEmpleadosDropdown)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 text-left bg-white hover:bg-gray-50 transition flex items-center justify-between"
          >
            <span className="text-sm text-gray-700">
              {empleadosSeleccionados.length === empleados.length
                ? "Todos los empleados"
                : `${empleadosSeleccionados.length} seleccionado${empleadosSeleccionados.length !== 1 ? 's' : ''}`}
            </span>
            <i className={`fas fa-chevron-${showEmpleadosDropdown ? 'up' : 'down'} text-gray-500`}></i>
          </button>

          {showEmpleadosDropdown && (
            <div className="absolute z-50 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                <button
                  onClick={toggleTodos}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm font-semibold text-gray-700"
                >
                  <i className={`fas fa-${empleadosSeleccionados.length === empleados.length ? 'check-square' : 'square'} mr-2`}></i>
                  Todos
                </button>
              </div>
              {empleados.map(emp => (
                <label
                  key={emp._id}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={empleadosSeleccionados.includes(emp._id)}
                    onChange={() => toggleEmpleado(emp._id)}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700">{emp.nombre} {emp.primerApellido || emp.apellidos}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Rango de fechas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Fecha inicio */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <i className="fas fa-calendar mr-1"></i>
              Fecha Inicio
            </label>
            <DatePicker
              selected={fechaInicio}
              onChange={handleFechaInicioChange}
              dateFormat="dd/MM/yyyy"
              showYearDropdown
              showMonthDropdown
              dropdownMode="scroll"
              yearDropdownItemNumber={15}
              scrollableYearDropdown
              minDate={new Date(1900, 0, 1)}
              maxDate={fechaFin ? fechaFin : new Date(2079, 11, 31)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-gray-400 focus:border-gray-500"
              placeholderText="Seleccionar fecha"
            />
          </div>

          {/* Fecha fin */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <i className="fas fa-calendar mr-1"></i>
              Fecha Fin
            </label>
            <DatePicker
              selected={fechaFin}
              onChange={handleFechaFinChange}
              dateFormat="dd/MM/yyyy"
              showYearDropdown
              showMonthDropdown
              dropdownMode="scroll"
              yearDropdownItemNumber={15}
              scrollableYearDropdown
              minDate={fechaInicio}
              maxDate={new Date(2079, 11, 31)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-gray-400 focus:border-gray-500"
              placeholderText="Seleccionar fecha"
            />
          </div>
        </div>

        {/* Botón de búsqueda */}
        <div className="flex justify-end">
          <button
            onClick={cargarDatos}
            disabled={!fechaInicio || !fechaFin || loading}
            className="px-6 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
          >
            <i className="fas fa-search"></i>
            {loading ? 'Cargando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {/* Leyenda */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <p className="text-xs font-semibold text-gray-600 mb-2">Leyenda:</p>
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded">E/S</span>
            <span className="text-gray-600">Fichaje completo</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded">E/S</span>
            <span className="text-gray-600">Entrada sin salida</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">V</span>
            <span className="text-gray-600">Vacaciones</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-red-100 text-red-800 rounded">A</span>
            <span className="text-gray-600">Ausencia</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-gray-50 text-gray-400 rounded">-</span>
            <span className="text-gray-600">Sin datos</span>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="text-center py-12">
          <i className="fas fa-spinner fa-spin text-4xl text-gray-500 mb-4"></i>
          <p className="text-gray-600">Cargando datos...</p>
        </div>
      ) : (
        <>
          {/* Vista de Calendario */}
          {vistaActual === "calendario" && (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full border-collapse text-sm" style={{ minWidth: "100%" }}>
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700 sticky left-0 bg-gray-100 z-20 min-w-[150px]">
                      Empleado
                    </th>
                    {fechas.map((fecha, idx) => (
                      <th
                        key={idx}
                        className="border border-gray-300 px-3 py-3 text-center font-semibold text-gray-700 min-w-[70px]"
                      >
                        {formatoDiaCabecera(fecha)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {empleadosFiltrados.length === 0 ? (
                    <tr>
                      <td colSpan={fechas.length + 1} className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                        <i className="fas fa-inbox text-4xl text-gray-300 mb-2"></i>
                        <p>No hay empleados seleccionados</p>
                      </td>
                    </tr>
                  ) : (
                    empleadosFiltrados.map(emp => (
                      <tr key={emp._id} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-4 py-3 font-medium text-gray-800 sticky left-0 bg-white z-10 shadow-sm min-w-[150px]">
                          {emp.nombre} {emp.primerApellido || emp.apellidos}
                        </td>
                        {fechas.map((fecha, idx) => {
                          const cellKey = `${emp._id}-${idx}`;
                          const estado = estadosCeldas[cellKey];
                          return (
                            <td
                              key={cellKey}
                              className="border border-gray-300 px-3 py-3 text-center relative"
                              onMouseEnter={(e) => handleMouseEnter(e, cellKey, estado)}
                              onMouseLeave={handleMouseLeave}
                              style={{ willChange: estado.tipo !== 'sin-datos' ? 'background-color' : 'auto' }}
                            >
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${estado.color}`}>
                                {estado.texto}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Vista de Tabla */}
          {vistaActual === "tabla" && (
            <>
              {datosTabla.length === 0 ? (
                <div className="p-8 text-center border border-gray-200 rounded-lg">
                  <i className="fas fa-inbox text-6xl text-gray-300 mb-3"></i>
                  <p className="text-gray-600 text-lg">No hay fichajes para mostrar</p>
                </div>
              ) : (
                <>
                  {/* Paginación arriba */}
                  <div className="flex items-center justify-between gap-2 md:gap-4 p-3 md:p-4 bg-gray-50 border border-gray-200 rounded-t-lg">
                    <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-gray-700">
                      <i className="fas fa-info-circle text-indigo-500"></i>
                      <span className="font-semibold">
                        {datosTabla.length > 0
                          ? `${inicio + 1}-${Math.min(datosTabla.length, fin)} / ${datosTabla.length}`
                          : "0-0 / 0"}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 md:gap-2">
                      <button
                        onClick={() => setPaginaActual(Math.max(1, paginaActual - 1))}
                        disabled={paginaActual === 1}
                        className="px-2 md:px-3 py-1.5 md:py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors flex items-center gap-1 text-sm md:text-base"
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>

                      <button
                        onClick={() => setPaginaActual(Math.min(totalPaginas, paginaActual + 1))}
                        disabled={paginaActual === totalPaginas || totalPaginas === 0}
                        className="px-2 md:px-3 py-1.5 md:py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors flex items-center gap-1 text-sm md:text-base"
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto -mx-4 md:mx-0 border-x border-gray-200">
                    <table className="w-full table-fixed min-w-[800px]">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                          <th className="p-2 md:p-4 text-left w-20 md:w-24 font-semibold text-gray-700 border-b-2 border-gray-200 text-xs md:text-sm">
                            <i className="fas fa-user-circle mr-1"></i><span className="hidden sm:inline">Perfil</span>
                          </th>
                          <th className="p-2 md:p-4 text-left w-40 md:w-48 font-semibold text-gray-700 border-b-2 border-gray-200 text-xs md:text-sm">
                            <i className="fas fa-user mr-1 md:mr-2"></i>Nombre
                          </th>
                          <th className="p-2 md:p-4 text-left w-28 md:w-32 font-semibold text-gray-700 border-b-2 border-gray-200 text-xs md:text-sm">
                            <i className="fas fa-calendar-day mr-1"></i>Fecha
                          </th>
                          <th className="p-2 md:p-4 text-left font-semibold text-gray-700 border-b-2 border-gray-200 text-xs md:text-sm">
                            <i className="fas fa-clock mr-1 md:mr-2"></i>Fichajes
                          </th>
                          <th className="p-2 md:p-4 text-center w-32 md:w-40 font-semibold text-gray-700 border-b-2 border-gray-200 text-xs md:text-sm">
                            <i className="fas fa-cog mr-1"></i>Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {datosPaginados.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            {/* Imagen de perfil */}
                            <td className="p-2 md:p-4 align-top">
                              {(() => {
                                const usuarioDatos = item.userId;
                                const nombreCompleto = usuarioDatos
                                  ? `${usuarioDatos.nombre || ""} ${usuarioDatos.apellidos || ""}`.trim()
                                  : "Desconocido";

                                const imagenPerfil = usuarioDatos?.imagenPerfil;

                                let urlImg = `https://ui-avatars.com/api/?name=${encodeURIComponent(nombreCompleto || "-")}&background=ddd&color=333&size=128`;
                                if (imagenPerfil && typeof imagenPerfil === "string" && imagenPerfil.trim() !== "") {
                                  if (/^https?:\/\//i.test(imagenPerfil)) {
                                    urlImg = imagenPerfil;
                                  } else {
                                    urlImg = `${BACKEND_URL}${imagenPerfil}`;
                                  }
                                }

                                return (
                                  <img
                                    src={urlImg}
                                    alt={nombreCompleto}
                                    className="w-12 h-12 md:w-20 md:h-20 rounded-full object-cover border-2 md:border-4 border-white shadow-md"
                                    onError={(e) => {
                                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(nombreCompleto || "-")}&background=ddd&color=333&size=128`;
                                    }}
                                  />
                                );
                              })()}
                            </td>

                            {/* Nombre y apellidos */}
                            <td className="p-2 md:p-4 align-top break-words whitespace-normal">
                              <div className="font-bold text-gray-800 text-sm md:text-lg">
                                {item.userId
                                  ? `${item.userId.nombre} ${item.userId.apellidos}`
                                  : "Desconocido"}
                              </div>
                              <div className="text-xs md:text-sm text-gray-500 flex items-center gap-1 mt-1">
                                <i className="fas fa-envelope text-xs"></i>
                                <span className="truncate">{item.userId?.email}</span>
                              </div>
                            </td>

                            {/* Fecha */}
                            <td className="p-2 md:p-4 align-top whitespace-normal">
                              <div className="flex flex-col md:flex-row items-start md:items-center gap-1 md:gap-2 text-gray-700">
                                <i className="fas fa-calendar text-gray-500 text-sm"></i>
                                <span className="font-semibold text-xs md:text-sm">{new Date(item.fecha).toLocaleDateString("es-ES")}</span>
                              </div>
                            </td>

                            {/* Todos los fichajes del día y vacaciones/ausencias */}
                            <td className="p-2 md:p-4 align-top break-words whitespace-normal">
                              <div className="flex flex-wrap gap-1.5 md:gap-2">
                                {/* Mostrar fichajes */}
                                {item.registros.map((reg, regIdx) => {
                                  const { icono, color, label } = getIconoYColor(reg.tipo);
                                  const hora = new Date(reg.fecha).toLocaleTimeString(
                                    "es-ES",
                                    {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    }
                                  );
                                  
                                  // Indicadores de estado
                                  const esManual = reg.esManual;
                                  const estaModificado = reg.estaModificado;
                                  
                                  return (
                                    <div key={regIdx}>
                                      {/* Badge del fichaje */}
                                      <div
                                        className={`${color} px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-semibold flex items-center gap-1 md:gap-2 shadow-sm`}
                                      >
                                        {icono}
                                        <span className="hidden sm:inline">{label}</span>
                                        <span className="font-mono">{hora}</span>
                                        
                                        {/* Indicadores */}
                                        {esManual && (
                                          <span className="ml-1 px-1.5 py-0.5 bg-purple-200 text-purple-700 rounded text-xs font-bold" title="Fichaje creado manualmente">
                                            M
                                          </span>
                                        )}
                                        {estaModificado && (
                                          <span className="ml-1 px-1.5 py-0.5 bg-yellow-200 text-yellow-700 rounded text-xs font-bold" title="Fichaje modificado">
                                            E
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                                
                                {/* Mostrar solicitud si existe */}
                                {item.solicitud && (
                                  <div className={`px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-semibold flex items-center gap-1 md:gap-2 shadow-sm ${
                                    item.solicitud.tipo === 'vacaciones' 
                                      ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                                      : 'bg-red-100 text-red-700 border border-red-300'
                                  }`}>
                                    <i className={`fas ${item.solicitud.tipo === 'vacaciones' ? 'fa-umbrella-beach' : 'fa-ban'}`}></i>
                                    <span className="hidden sm:inline">
                                      {item.solicitud.tipo === 'vacaciones' ? 'Vacaciones' : 'Ausencia'}
                                    </span>
                                    {item.solicitud.motivo && (
                                      <span className="hidden md:inline text-xs opacity-90">
                                        - {item.solicitud.motivo}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                            
                            {/* Columna de acciones */}
                            <td className="p-2 md:p-4 align-top text-center">
                              <button
                                onClick={() => setModalAcciones({
                                  userId: item.userId,
                                  fecha: item.fecha,
                                  registros: item.registros
                                })}
                                className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm font-medium transition flex items-center gap-2"
                              >
                                <i className="fas fa-cog"></i>
                                Acciones
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginación abajo */}
                  <div className="flex justify-center items-center gap-1 md:gap-2 p-3 md:p-4 bg-gray-50 border border-gray-200 rounded-b-lg">
                    <button
                      onClick={() => setPaginaActual(Math.max(1, paginaActual - 1))}
                      disabled={paginaActual === 1}
                      className="px-2 md:px-3 py-1.5 md:py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors flex items-center gap-1 text-sm md:text-base"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>

                    {generarNumeroPaginas().map((num) => (
                      <button
                        key={num}
                        onClick={() => setPaginaActual(num)}
                        className={`px-2 md:px-3 py-1.5 md:py-2 border border-gray-300 rounded-lg transition-colors text-sm md:text-base ${
                          paginaActual === num
                            ? "bg-gray-800 text-white border-gray-800 shadow-md"
                            : "hover:bg-gray-100"
                        }`}
                      >
                        {num}
                      </button>
                    ))}

                    {totalPaginas > generarNumeroPaginas()[generarNumeroPaginas().length - 1] && (
                      <span className="text-gray-400 px-1 md:px-2 text-sm">...</span>
                    )}

                    <button
                      onClick={() => setPaginaActual(Math.min(totalPaginas, paginaActual + 1))}
                      disabled={paginaActual === totalPaginas}
                      className="px-2 md:px-3 py-1.5 md:py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors flex items-center gap-1 text-sm md:text-base"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Tooltip personalizado con optimizaciones CSS */}
      {hoveredCell && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)',
            willChange: 'transform' // Optimización para animaciones
          }}
        >
          <div className="bg-white text-gray-900 px-4 py-3 rounded-lg shadow-2xl border-2 border-gray-200 text-sm whitespace-pre-line max-w-xs">
            <div className="relative font-medium">
              {hoveredCell.estado.tooltip}
              {/* Flecha del tooltip */}
              <div 
                className="absolute left-1/2 -bottom-4 w-3 h-3 bg-white border-r-2 border-b-2 border-gray-200 transform rotate-45 -translate-x-1/2"
                style={{ willChange: 'transform' }}
              ></div>
            </div>
          </div>
        </div>
      )}
      
      {/* Overlay de loading durante exportación - Bloquea interacción */}
      {loadingExport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-2xl p-8 text-center max-w-sm">
            <div className="mb-4">
              <i className="fas fa-file-export text-6xl text-gray-500 animate-pulse"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Generando archivo...</h3>
            <p className="text-gray-600 mb-4">Por favor, espera mientras se procesa la exportación</p>
            <div className="flex justify-center gap-2">
              <div className="w-3 h-3 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-3 h-3 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-3 h-3 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Mensaje de notificación */}
      {mensaje.texto && (
        <div className={`fixed top-4 right-4 z-[70] px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 animate-fade-in ${
          mensaje.tipo === 'exito' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          <i className={`fas ${mensaje.tipo === 'exito' ? 'fa-check-circle' : 'fa-exclamation-circle'} text-2xl`}></i>
          <span className="font-medium">{mensaje.texto}</span>
        </div>
      )}

      {/* Modal Crear Fichaje Manual */}
      {modalCrearFichaje && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[65] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="bg-purple-600 p-4 rounded-t-lg flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <i className="fas fa-plus-circle"></i>
                Crear Fichaje Manual
              </h3>
              <button
                onClick={() => setModalCrearFichaje(false)}
                className="text-white hover:bg-purple-700 rounded p-2"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleCrearFichaje} className="p-6 space-y-4">
              {/* Empleado */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Empleado *
                </label>
                <select
                  value={formCrear.empleadoId}
                  onChange={(e) => setFormCrear({ ...formCrear, empleadoId: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="">Seleccionar empleado</option>
                  {empleados.map(emp => (
                    <option key={emp._id} value={emp._id}>
                      {emp.nombre} {emp.primerApellido} {emp.segundoApellido}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tipo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo *
                </label>
                <select
                  value={formCrear.tipo}
                  onChange={(e) => setFormCrear({ ...formCrear, tipo: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="entrada">Entrada</option>
                  <option value="salida">Salida</option>
                </select>
              </div>

              {/* Fecha */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha *
                </label>
                <input
                  type="date"
                  value={formCrear.fecha}
                  onChange={(e) => setFormCrear({ ...formCrear, fecha: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  min="1900-01-01"
                  max="2079-12-31"
                  required
                />
              </div>

              {/* Hora */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Hora *
                </label>
                <input
                  type="time"
                  value={formCrear.hora}
                  onChange={(e) => setFormCrear({ ...formCrear, hora: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              {/* Motivo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Motivo *
                </label>
                <textarea
                  value={formCrear.motivo}
                  onChange={(e) => setFormCrear({ ...formCrear, motivo: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 resize-none"
                  rows="3"
                  placeholder="Indica el motivo de crear este fichaje manualmente..."
                  required
                />
              </div>

              {/* Botones */}
              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setModalCrearFichaje(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
                >
                  {loading ? 'Creando...' : 'Crear Fichaje'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Fichaje */}
      {modalEditarFichaje && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[65] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="bg-blue-600 p-4 rounded-t-lg flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <i className="fas fa-edit"></i>
                Editar Fichaje
              </h3>
              <button
                onClick={() => setModalEditarFichaje(null)}
                className="text-white hover:bg-blue-700 rounded p-2"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleEditarFichaje} className="p-6 space-y-4">
              {/* Tipo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo
                </label>
                <select
                  value={formEditar.tipo}
                  onChange={(e) => setFormEditar({ ...formEditar, tipo: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="entrada">Entrada</option>
                  <option value="salida">Salida</option>
                </select>
              </div>

              {/* Fecha */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha
                </label>
                <input
                  type="date"
                  value={formEditar.fecha}
                  onChange={(e) => setFormEditar({ ...formEditar, fecha: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  min="1900-01-01"
                  max="2079-12-31"
                />
              </div>

              {/* Hora */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Hora
                </label>
                <input
                  type="time"
                  value={formEditar.hora}
                  onChange={(e) => setFormEditar({ ...formEditar, hora: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Motivo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Motivo de la modificación *
                </label>
                <textarea
                  value={formEditar.motivo}
                  onChange={(e) => setFormEditar({ ...formEditar, motivo: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 resize-none"
                  rows="3"
                  placeholder="Indica el motivo de esta modificación..."
                  required
                />
              </div>

              {/* Botones */}
              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setModalEditarFichaje(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Eliminar Fichaje */}
      {modalEliminarFichaje && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[65] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="bg-red-600 p-4 rounded-t-lg flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <i className="fas fa-trash-alt"></i>
                Eliminar Fichaje
              </h3>
              <button
                onClick={() => setModalEliminarFichaje(null)}
                className="text-white hover:bg-red-700 rounded p-2"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
                <div>
                  <p className="text-gray-800 font-medium">¿Estás seguro de eliminar este fichaje?</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Esta acción quedará registrada en el historial de modificaciones.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Motivo de eliminación *
                </label>
                <textarea
                  value={motivoEliminar}
                  onChange={(e) => setMotivoEliminar(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 resize-none"
                  rows="3"
                  placeholder="Indica el motivo de eliminar este fichaje..."
                  required
                />
              </div>

              {/* Botones */}
              <div className="flex gap-3 justify-end pt-4">
                <button
                  onClick={() => setModalEliminarFichaje(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleEliminarFichaje}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                >
                  {loading ? 'Eliminando...' : 'Confirmar Eliminación'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historial de Modificaciones */}
      {modalHistorial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[65] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gray-800 p-4 rounded-t-lg flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <i className="fas fa-history"></i>
                Historial de Modificaciones
              </h3>
              <button
                onClick={() => setModalHistorial(null)}
                className="text-white hover:bg-gray-700 rounded p-2"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {loadingHistorial ? (
                <div className="text-center py-8">
                  <i className="fas fa-spinner fa-spin text-4xl text-gray-400 mb-3"></i>
                  <p className="text-gray-600">Cargando historial...</p>
                </div>
              ) : historialModificaciones.length === 0 ? (
                <div className="text-center py-8">
                  <i className="fas fa-inbox text-6xl text-gray-300 mb-3"></i>
                  <p className="text-gray-600 text-lg">No hay modificaciones registradas</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {historialModificaciones.map((mod, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            mod.tipoModificacion === 'crear' ? 'bg-green-100 text-green-700' :
                            mod.tipoModificacion === 'editar' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {mod.tipoModificacion === 'crear' ? 'CREADO' :
                             mod.tipoModificacion === 'editar' ? 'EDITADO' : 'ELIMINADO'}
                          </span>
                          <span className="text-sm text-gray-600">
                            {mod.campoModificado !== 'completo' && `Campo: ${mod.campoModificado}`}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(mod.fechaModificacion).toLocaleString('es-ES')}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        {mod.valorAnterior && (
                          <div className="bg-red-50 border border-red-200 rounded p-2">
                            <p className="text-xs font-semibold text-red-700 mb-1">Valor Anterior:</p>
                            <p className="text-sm text-gray-800">
                              {typeof mod.valorAnterior === 'object' 
                                ? JSON.stringify(mod.valorAnterior, null, 2)
                                : mod.valorAnterior instanceof Date 
                                  ? new Date(mod.valorAnterior).toLocaleString('es-ES')
                                  : String(mod.valorAnterior)}
                            </p>
                          </div>
                        )}
                        {mod.valorNuevo && (
                          <div className="bg-green-50 border border-green-200 rounded p-2">
                            <p className="text-xs font-semibold text-green-700 mb-1">Valor Nuevo:</p>
                            <p className="text-sm text-gray-800">
                              {typeof mod.valorNuevo === 'object' 
                                ? JSON.stringify(mod.valorNuevo, null, 2)
                                : mod.valorNuevo instanceof Date 
                                  ? new Date(mod.valorNuevo).toLocaleString('es-ES')
                                  : String(mod.valorNuevo)}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="bg-gray-50 border border-gray-200 rounded p-3">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Motivo:</p>
                        <p className="text-sm text-gray-800">{mod.motivo}</p>
                      </div>

                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                        <i className="fas fa-user"></i>
                        <span>
                          Modificado por: {mod.modificadoPor?.nombre} {mod.modificadoPor?.primerApellido}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Acciones - Diseño Minimalista */}
      {modalAcciones && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header minimalista */}
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {modalAcciones.userId.nombre} {modalAcciones.userId.primerApellido}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {new Date(modalAcciones.fecha).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <button
                  onClick={() => setModalAcciones(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                >
                  <i className="fas fa-times text-lg"></i>
                </button>
              </div>
            </div>

            {/* Body scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Sección de Entradas */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Entradas</h4>
                  <button
                    onClick={() => {
                      setFormCrear({
                        empleadoId: modalAcciones.userId._id,
                        tipo: 'entrada',
                        fecha: toLocalDateStr(modalAcciones.fecha),
                        hora: '08:00',
                        motivo: ''
                      });
                      setModalCrearFichaje(true);
                      setModalAcciones(null);
                    }}
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors flex items-center gap-1.5"
                  >
                    <i className="fas fa-plus text-xs"></i>
                    Añadir
                  </button>
                </div>

                {modalAcciones.registros.filter(r => r.tipo === 'entrada').length === 0 ? (
                  <div className="text-center py-10 text-gray-400">
                    <i className="fas fa-clock text-2xl mb-2 opacity-30"></i>
                    <p className="text-sm">Sin entradas</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {modalAcciones.registros
                      .filter(r => r.tipo === 'entrada')
                      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
                      .map((fichaje) => {
                        const hora = new Date(fichaje.fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                        return (
                          <div key={fichaje._id} className="group bg-gray-50 hover:bg-emerald-50 border border-gray-200 hover:border-emerald-200 rounded-xl p-3 transition-all">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600">
                                  <i className="fas fa-arrow-right-to-bracket"></i>
                                </div>
                                <div>
                                  <p className="font-mono text-lg font-semibold text-gray-900">{hora}</p>
                                  <div className="flex gap-1.5 mt-0.5">
                                    {fichaje.esManual && (
                                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded text-xs font-medium">Manual</span>
                                    )}
                                    {fichaje.estaModificado && (
                                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-600 rounded text-xs font-medium">Editado</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => {
                                    abrirModalEditar(fichaje);
                                    setModalAcciones(null);
                                  }}
                                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Editar"
                                >
                                  <i className="fas fa-pen text-sm"></i>
                                </button>
                                <button
                                  onClick={() => {
                                    setModalEliminarFichaje(fichaje);
                                    setModalAcciones(null);
                                  }}
                                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Eliminar"
                                >
                                  <i className="fas fa-trash-alt text-sm"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Separador sutil */}
              <div className="border-t border-gray-100 my-6"></div>

              {/* Sección de Salidas */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Salidas</h4>
                  <button
                    onClick={() => {
                      setFormCrear({
                        empleadoId: modalAcciones.userId._id,
                        tipo: 'salida',
                        fecha: toLocalDateStr(modalAcciones.fecha),
                        hora: '17:00',
                        motivo: ''
                      });
                      setModalCrearFichaje(true);
                      setModalAcciones(null);
                    }}
                    className="text-sm text-rose-600 hover:text-rose-700 font-medium transition-colors flex items-center gap-1.5"
                  >
                    <i className="fas fa-plus text-xs"></i>
                    Añadir
                  </button>
                </div>

                {modalAcciones.registros.filter(r => r.tipo === 'salida').length === 0 ? (
                  <div className="text-center py-10 text-gray-400">
                    <i className="fas fa-clock text-2xl mb-2 opacity-30"></i>
                    <p className="text-sm">Sin salidas</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {modalAcciones.registros
                      .filter(r => r.tipo === 'salida')
                      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
                      .map((fichaje) => {
                        const hora = new Date(fichaje.fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                        return (
                          <div key={fichaje._id} className="group bg-gray-50 hover:bg-rose-50 border border-gray-200 hover:border-rose-200 rounded-xl p-3 transition-all">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-rose-100 text-rose-600">
                                  <i className="fas fa-arrow-right-from-bracket"></i>
                                </div>
                                <div>
                                  <p className="font-mono text-lg font-semibold text-gray-900">{hora}</p>
                                  <div className="flex gap-1.5 mt-0.5">
                                    {fichaje.esManual && (
                                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded text-xs font-medium">Manual</span>
                                    )}
                                    {fichaje.estaModificado && (
                                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-600 rounded text-xs font-medium">Editado</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => {
                                    abrirModalEditar(fichaje);
                                    setModalAcciones(null);
                                  }}
                                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Editar"
                                >
                                  <i className="fas fa-pen text-sm"></i>
                                </button>
                                <button
                                  onClick={() => {
                                    setModalEliminarFichaje(fichaje);
                                    setModalAcciones(null);
                                  }}
                                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Eliminar"
                                >
                                  <i className="fas fa-trash-alt text-sm"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>

            {/* Footer minimalista */}
            <div className="px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setModalAcciones(null)}
                className="w-full sm:w-auto px-6 py-2.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg font-medium transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}